import React, { useEffect, useState, useMemo } from "react";
import "@styles/pages/GenericPage/BlogArticlePage.scss";
import { Box, Typography } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import GenericSwiper from "@components/ui/GenericSwiper";
import {
  getBlogById,
  addArticleLike,
  addClickRate,
  getRelateVideoList,
} from "@api/landingApi";
import LoadingButton from "@mui/lab/LoadingButton";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";

const BlogArticlePage = () => {
  const { t, i18n } = useTranslation();
  const isChinese = i18n.language === "hk";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blog_id = searchParams.get("blog_id") || "";
  const [html, setHtml] = useState("");
  const [allowLike, setAllowLike] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // 相關 Video
  const [relateVideoList, setRelateVideoList] = useState([]);

  const fetchRelateVideoByHashtag = async (hashtag) => {
    setRelateVideoList([]);
    if (!hashtag) return;
    try {
      const res = await getRelateVideoList({ hashtag });
      console.log(res);
      setRelateVideoList(res.data?.Data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetechBlogById = async () => {
    try {
      const res = await getBlogById({ blog_id });
      const data = res.data.Data;
      setAllowLike(data?.allow_like);
      setLikeCount(data?.total_like);
      setHtml(data);
      fetchRelateVideoByHashtag(data?.hashtag);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    fetechBlogById();
  }, []);

  const postClickRate = async ({ id, article_type }) => {
    try {
      const res = await addClickRate({ id, article_type });
      console.log("Click tracked:", res.data.StatusCode);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    postClickRate({ id: blog_id, article_type: "blog" });
  }, []);

  const postAddArticleLike = async () => {
    setIsLoading(true);
    try {
      const res = await addArticleLike({ id: blog_id, article_type: "blog" });
      console.log(res);
      setAllowLike(false);
      setLikeCount((prev) => prev + 1);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    }
  };
  const handleCopyUrl = () => {
    const fullUrl = window.location.href;
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast.success(t("generic:link_copied"));
    });
  };

  const handleShareWhatsApp = () => {
    const title = isChinese ? html.title_hk : html.title_hk_en;
    const url = window.location.href;
    const shareText = encodeURIComponent(`【${title}】\n${url}`);
    window.open(`https://wa.me/?text=${shareText}`, "_blank");
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(facebookUrl, "_blank");
  };

  const pageTitle = isChinese ? html.title_hk : html.title_hk_en;
  const pageDesc = isChinese
    ? html.html_hk?.replace(/<[^>]*>/g, "").slice(0, 100)
    : html.html_hk_en?.replace(/<[^>]*>/g, "").slice(0, 100);

  const videoSlides = useMemo(
    () =>
      relateVideoList.map((s) => ({
        ...s,
        id: s.id,
        image: s.thumbnail_hk
          ? isChinese
            ? s.thumbnail_hk
            : s.thumbnail_hk_en
          : s.thumbnail || s.image,
        title: isChinese ? s.title_hk || s.title : s.title_hk_en || s.title,
        description: isChinese
          ? s.desc_hk || s.description
          : s.desc_hk_en || s.description,
        url: s?.url,
        hashtag: s?.hashtag,
      })),
    [relateVideoList, isChinese]
  );

  return (
    <Box className="blog-article-container">
      {/* --- Helmet meta tags for Facebook share --- */}
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <ArrowBackIcon
        className="back-icon"
        onClick={() => {
          navigate("/blogs");
        }}
      />
      <Box className="article-trainer-container">
        <Box className="article-title-description-container">
          <Box className="article-title">
            {isChinese ? html.title_hk : html.title_hk_en}
          </Box>
        </Box>
      </Box>
      <Box className="icon-container">
        <Box className="trainer-container">
          <img className="trainer-image" src={html.trainer_img}></img>
          <Typography className="trainer-name">
            {isChinese ? html.trainer_name_hk : html.trainer_name_hk_en}
          </Typography>
        </Box>
        <Box className="media-container">
          {" "}
          <LoadingButton
            className="like-icon-container"
            disabled={!allowLike || isLoading}
            loading={isLoading}
            onClick={() => {
              allowLike && postAddArticleLike();
            }}
          >
            <Typography className="like-text">{likeCount}</Typography>
            <>
              {allowLike ? (
                <FavoriteBorderIcon className="like-icon" />
              ) : (
                <FavoriteIcon className="like-icon" />
              )}
            </>
          </LoadingButton>
          <WhatsAppIcon className="icon" onClick={handleShareWhatsApp} />
          <FacebookIcon className="icon" onClick={handleShareFacebook} />
          <FileCopyIcon className="icon" onClick={handleCopyUrl} />
        </Box>
      </Box>
      {(html.kv_hk || html.kv_hk_en) && (
        <img
          className="article-image"
          src={isChinese ? html.kv_hk : html.kv_hk_en}
        ></img>
      )}

      <Box
        className="article-container"
        dangerouslySetInnerHTML={{
          __html: isChinese ? html.html_hk : html.html_hk_en,
        }}
      ></Box>
      {/* Swiper */}
      {videoSlides.length > 0 ? (
        <Box className="related-section">
          <Typography className="related-title">
            <Trans i18nKey="generic:related_video" />
          </Typography>
          <GenericSwiper type="video" slides={videoSlides} />
        </Box>
      ) : (
        <Typography className="coming-soon-text" variant="h6">
          {/* <Trans i18nKey="home:coming_soon" /> */}
        </Typography>
      )}
    </Box>
  );
};

export default BlogArticlePage;
